import { useMediaQuery } from "@mui/material";
import {
  CHART_HEIGHT,
  CHART_WIDTH,
} from "reports/components/Reports/BespokeReports/bespokeReportUtils";
import { customDateTick } from "reports/components/Reports/RechartHelpers/rechartTickUtils";
import { round } from "lodash";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Label,
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  LabelList,
  Line,
} from "recharts";
import {
  BREAKPOINT_MOBILE,
  FONT_SIZE_EXTRA_SMALL,
  FONT_SIZE_SMALL,
} from "../../../../constants/constants";

import { GenericRechartsProps } from "../../../../types";
import RotatedYAxisLabel from "../../RechartHelpers/RotatedYAxisLabel";
import { GenericComposedChartStyles } from "./barChartStyles";

/**
 * Renders a custom label
 * @param x horizontal positiom
 * @param y vertical position
 * @param width text box width
 * @param height text box height
 * @param value text content
 */
const renderCustomBarLabel =
  (isPrint = false) =>
  ({ x, y, width, height, value }) => {
    return (
      <text
        key={Math.random() * 1000}
        x={x + width}
        y={y + height}
        fill="#666"
        fontSize={isPrint ? FONT_SIZE_SMALL : FONT_SIZE_EXTRA_SMALL}
        textAnchor="left"
      >
        {value}
      </text>
    );
  };

/**
 * Renders a custom bar label on the left
 * @param x horizontal positiom
 * @param y vertical position
 * @param value text content
 */
const renderCustomBarLabelLeft =
  (isPrint = false) =>
  ({ x, y, value }) => {
    return (
      <text
        key={Math.random() * 1000}
        x={x - 30}
        y={y + FONT_SIZE_EXTRA_SMALL + 3}
        fill="#666"
        fontSize={isPrint ? FONT_SIZE_SMALL : FONT_SIZE_EXTRA_SMALL}
        textAnchor="right"
      >
        {value}
      </text>
    );
  };

/**
 * Renders a line label
 * @param x horizontal positiom
 * @param y vertical position
 * @param stroke text colour
 * @param value text content
 */
export const renderCustomLineLabel =
  (isPrint = false) =>
  ({ x, y, stroke, value }) => {
    return (
      <text
        x={x}
        y={y}
        dy={-4}
        fill={stroke}
        fontSize={isPrint ? FONT_SIZE_SMALL : FONT_SIZE_EXTRA_SMALL}
        textAnchor="middle"
      >
        {value}
      </text>
    );
  };

/**
 * Renders a custom bar with rounded corners and a line under the bar to match the x-axis thickness
 * @param x horizontal positiom
 * @param y vertical position
 * @param width bar width
 * @param height bar height
 * @param fill bar color
 */
export const renderCustomBar =
  (chartHeight: number) =>
  ({ x, y, width, height, fill }) => {
    const fixedChartHeight = chartHeight - 2 * TOP_BOTTOM_CHART_PADDING;
    return (
      <g>
        <rect
          stroke="#000"
          strokeWidth="0"
          {...{ width, height, x, y, fill }}
          rx={2}
          ry={2}
        />
        <line
          y2={fixedChartHeight}
          x2={x + width}
          y1={fixedChartHeight}
          x1={x}
          stroke="#000"
          fill="none"
          strokeWidth="1"
        />
      </g>
    );
  };

export const TOP_BOTTOM_CHART_PADDING = 30;
export const LEFT_RIGHT_CHART_PADDING = TOP_BOTTOM_CHART_PADDING * 2;
const LEFT_RIGHT_CHART_PADDING_MOBILE = 5;
const LEFT_RIGHT_VERTICAL_CHART_PADDING = 5;
const LEFT_RIGHT_VERTICAL_CHART_PADDING_MOBILE = 30;
const PADDING_PER_CHAR = 5;
const PX_PER_CHAR_VERTICAL = 8;
export const MAX_BAR_SIZE = 75;
export const TOP_XAXIS_PADDING = 50;

function getMaxNumberOfChars({
  data = [],
  dataKey,
}: {
  data?: any[] | null;
  dataKey: string;
}) {
  const numberOfChars = data?.map(d => String(d[dataKey]).length) ?? [0];
  return Math.max(...numberOfChars);
}

/**
 * generate and create bar chart based on given data
 */
export default function GenericComposedChart({
  title,
  data,
  xLabel = "",
  xKey = "date",
  xAxisCustomProps,
  yLabel,
  yKey,
  bars = [],
  vertical = false,
  width = CHART_WIDTH,
  height = CHART_HEIGHT,
  useGrid = true,
  referenceAreas = [{} as any],
  referencelines = [{} as any],
  targetLines = [{} as any],
  targetPoints,
  legend,
  customBars,
  tooltipContent,
  tooltipLabelFormatter = null,
  yAxisMaxCharsPerLine,
  yAxisDomain,
  xAxisDomain,
}: GenericRechartsProps) {
  const isMobileOrSmaller = useMediaQuery(
    `(max-width: ${BREAKPOINT_MOBILE}px)`
  );
  const isPrint = useMediaQuery("print");

  const xAxisStartDomain = xAxisDomain?.[0] ?? 0;
  const xAxisEndDomain = (dataMax: number) =>
    round((xAxisDomain?.[1] ?? dataMax) * 1.25, 0);

  // Use max yAxis tick length (number of chars for values in y-axis if ykey is supplied) for yAxis width
  const yKeyVerticalChartWidth =
    yAxisMaxCharsPerLine ||
    getMaxNumberOfChars({
      data,
      dataKey: yKey ?? "",
    });

  // Get number of characters for each data point and use max to determine yAxis left padding
  const dataKey = bars?.[0]?.key ?? customBars?.props?.dataKey;
  const maxNumOfBarValueChars = getMaxNumberOfChars({
    data,
    dataKey,
  });

  const leftRightChartPadding = isMobileOrSmaller
    ? LEFT_RIGHT_CHART_PADDING_MOBILE
    : LEFT_RIGHT_CHART_PADDING;

  const leftRightVerticalChartPadding = isMobileOrSmaller
    ? LEFT_RIGHT_VERTICAL_CHART_PADDING_MOBILE
    : LEFT_RIGHT_VERTICAL_CHART_PADDING;

  const verticalChartLeftPadding =
    yLabel === "" ? leftRightChartPadding : leftRightVerticalChartPadding;

  const chartLeftPadding = vertical
    ? verticalChartLeftPadding
    : maxNumOfBarValueChars * PADDING_PER_CHAR;

  return (
    <GenericComposedChartStyles hideYAxis={true}>
      <p className="chartTitle" key={title + ": actualTitle"}>
        {title}
      </p>
      <div className="chartBody" key={title + ": wrapper"}>
        <ComposedChart
          key={title}
          width={width * 0.95}
          data={data || undefined}
          {...{ height }}
          margin={{
            top: TOP_BOTTOM_CHART_PADDING,
            right: vertical
              ? leftRightVerticalChartPadding
              : leftRightChartPadding,
            left: chartLeftPadding,
            bottom: TOP_BOTTOM_CHART_PADDING,
          }}
          stackOffset="sign"
          layout={vertical ? "vertical" : "horizontal"}
        >
          {useGrid ? (
            <CartesianGrid vertical={vertical} horizontal={!vertical} />
          ) : null}
          {legend}
          {vertical ? (
            <YAxis
              type="category"
              dataKey={yKey}
              tick={true}
              label={<RotatedYAxisLabel height={height} value={yLabel} />}
              width={yKeyVerticalChartWidth * PX_PER_CHAR_VERTICAL}
              interval={0}
              tickMargin={30}
            />
          ) : (
            <XAxis
              key={xKey}
              dataKey={xKey}
              tick={customDateTick(isPrint)}
              interval={0}
            >
              <Label
                key={xLabel}
                value={xLabel}
                offset={-15}
                fontSize={isPrint ? FONT_SIZE_SMALL : FONT_SIZE_EXTRA_SMALL}
                position="insideBottom"
              />
            </XAxis>
          )}
          {!vertical && xAxisCustomProps && (
            <XAxis
              dataKey={xAxisCustomProps.xKey}
              tick={xAxisCustomProps.customTicks}
              axisLine={false}
              tickLine={false}
              interval={0}
              xAxisId="dateId"
              scale="auto"
              type="category"
            />
          )}
          {vertical ? (
            <XAxis
              type="number"
              domain={[xAxisStartDomain, dataMax => xAxisEndDomain(dataMax)]}
            >
              <Label value={xLabel} offset={-15} position="insideBottom" />
            </XAxis>
          ) : (
            <YAxis
              type="number"
              orientation="left"
              label={<RotatedYAxisLabel height={height} value={yLabel} />}
              domain={yAxisDomain ? yAxisDomain : ["auto", "auto"]}
              allowDataOverflow={true}
            />
          )}

          <Tooltip
            labelFormatter={tooltipLabelFormatter}
            content={tooltipContent}
          />
          {!customBars
            ? bars?.map(
                ({
                  key,
                  name,
                  stackId,
                  fill,
                  label,
                  labelContents,
                  label2,
                  label2Contents,
                }) => {
                  return (
                    <Bar
                      key={key}
                      dataKey={key}
                      name={name}
                      stackId={stackId}
                      fill={fill}
                      isAnimationActive={false}
                      maxBarSize={MAX_BAR_SIZE}
                      shape={renderCustomBar(height)}
                    >
                      {label ? (
                        !vertical ? (
                          <LabelList
                            position="top"
                            fontSize={
                              isPrint ? FONT_SIZE_SMALL : FONT_SIZE_EXTRA_SMALL
                            }
                          />
                        ) : (
                          <LabelList
                            dataKey={labelContents}
                            content={renderCustomBarLabel(isPrint) as any}
                            position="right"
                            fontSize={
                              isPrint ? FONT_SIZE_SMALL : FONT_SIZE_EXTRA_SMALL
                            }
                          />
                        )
                      ) : null}
                      {label2 ? (
                        !vertical ? (
                          <LabelList position="top" />
                        ) : (
                          <LabelList
                            dataKey={label2Contents}
                            content={renderCustomBarLabelLeft(isPrint) as any}
                            position="left"
                          />
                        )
                      ) : null}
                    </Bar>
                  );
                }
              )
            : customBars}
          {targetPoints}
          {targetLines?.map(({ name, stroke, key, hideLabel, fillDot }) => {
            return (
              <Line
                key={key + Math.random()}
                type="linear"
                dot={fillDot ? { fill: stroke } : true}
                name={name}
                {...{
                  label: hideLabel ? "" : renderCustomLineLabel(isPrint),
                  strokeDashArray: "2 2",
                }} // recharts 2.0.8-9 typescript bug
                dataKey={key}
                stroke={stroke}
                isAnimationActive={false}
              />
            );
          })}
          {referencelines.map(
            ({ y, label, stroke, strokeDasharray, isFront, alwaysShow }) => {
              const key = label + y + stroke;
              return (
                <ReferenceLine
                  key={isNaN(key) ? Math.random() * 1000 : key}
                  label={label}
                  y={y}
                  ifOverflow={alwaysShow ? "extendDomain" : "hidden"}
                  isFront={isFront}
                  stroke={stroke}
                  strokeDasharray={strokeDasharray}
                />
              );
            }
          )}
          {referenceAreas.map(
            ({ label, y, x, x2, stroke, strokeDasharray }) => {
              const key = label + y + x + x2 + stroke;
              return (
                <ReferenceArea
                  key={isNaN(key) ? Math.random() * 1000 : key}
                  label={label}
                  y1={y}
                  y2={y * 1.000001}
                  x1={x}
                  x2={x2}
                  stroke={stroke}
                  strokeDasharray={strokeDasharray}
                />
              );
            }
          )}
        </ComposedChart>
      </div>
    </GenericComposedChartStyles>
  );
}
